import React from 'react';
import { AuditLoggers } from '../../../utils/auditLogger';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Close as CloseIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { isDocParserFamily } from '../../../utils/parserTypes';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const MasterDataDrawer = ({ open, onClose, fundData }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.MASTER_DATA);
  // State for Terms (both include and exclude)
  const [terms, setTerms] = React.useState([]);
  const [termsLoading, setTermsLoading] = React.useState(false);
  const [termsError, setTermsError] = React.useState(null);

  // State for Terms Add Dialog
  const [addTermDialogOpen, setAddTermDialogOpen] = React.useState(false);
  const [newTerm, setNewTerm] = React.useState('');
  const [newTermType, setNewTermType] = React.useState('include'); // 'include' or 'exclude'
  const [addTermLoading, setAddTermLoading] = React.useState(false);
  const [addTermError, setAddTermError] = React.useState(null);

  // State for Terms Edit Dialog
  const [editTermDialogOpen, setEditTermDialogOpen] = React.useState(false);
  const [editingTerm, setEditingTerm] = React.useState(null);
  const [editTermValue, setEditTermValue] = React.useState('');
  const [editTermType, setEditTermType] = React.useState('include');
  const [editTermLoading, setEditTermLoading] = React.useState(false);
  const [editTermError, setEditTermError] = React.useState(null);
  const [selectedTermRow, setSelectedTermRow] = React.useState(null);

  // State for Terms Delete Dialog
  const [deleteTermDialogOpen, setDeleteTermDialogOpen] = React.useState(false);
  const [termToDelete, setTermToDelete] = React.useState(null);
  const [termToDeleteType, setTermToDeleteType] = React.useState(null);
  const [deleteTermLoading, setDeleteTermLoading] = React.useState(false);

  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // State for Document Type Add Dialog
  const [addDocTypeDialogOpen, setAddDocTypeDialogOpen] = React.useState(false);
  const [newDocType, setNewDocType] = React.useState('');
  const [newParserId, setNewParserId] = React.useState('');
  const [newParserName, setNewParserName] = React.useState(''); // For dropdown selection
  const [newExtractorName, setNewExtractorName] = React.useState(''); // For extractor dropdown selection
  const [addDocTypeLoading, setAddDocTypeLoading] = React.useState(false);
  const [addDocTypeError, setAddDocTypeError] = React.useState(null);

  // State for Workflow dropdown
  const [workflowOptions, setWorkflowOptions] = React.useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = React.useState('');
  const [selectedWorkflowParserType, setSelectedWorkflowParserType] = React.useState(null);
  const [workflowLoading, setWorkflowLoading] = React.useState(false);
  const [workflowError, setWorkflowError] = React.useState(null);

  // State for Document Type Edit Dialog
  const [editDocTypeDialogOpen, setEditDocTypeDialogOpen] = React.useState(false);
  const [editingDocType, setEditingDocType] = React.useState(null);
  const [editParserId, setEditParserId] = React.useState('');
  const [editParserName, setEditParserName] = React.useState(''); // For dropdown selection
  const [editExtractorName, setEditExtractorName] = React.useState(''); // For extractor dropdown selection
  const [editDocTypeLoading, setEditDocTypeLoading] = React.useState(false);
  const [editDocTypeError, setEditDocTypeError] = React.useState(null);
  const [documentTypeOptions, setDocumentTypeOptions] = React.useState([]);
  const [documentTypeOptionsLoading, setDocumentTypeOptionsLoading] = React.useState(false);
  const [documentTypeOptionsError, setDocumentTypeOptionsError] = React.useState(null);

  // State for Parser Options
  const [parserOptions, setParserOptions] = React.useState([]);
  const [parserOptionsLoading, setParserOptionsLoading] = React.useState(false);
  const [parserOptionsError, setParserOptionsError] = React.useState(null);

  // Hardcoded extractor name since we don't need the dropdown
  const [extractorOptions] = React.useState([{ id: 1, extractorName: 'doc_parser' }]);
  const [extractorOptionsLoading] = React.useState(false);
  const [extractorOptionsError] = React.useState(null);

  // State for Document Types
  const [documentTypesData, setDocumentTypesData] = React.useState([]);
  const [documentTypesLoading, setDocumentTypesLoading] = React.useState(false);
  const [documentTypesError, setDocumentTypesError] = React.useState(null);
  const [selectedDocTypeRow, setSelectedDocTypeRow] = React.useState(null);

  // State for Document Type Delete Dialog
  const [deleteDocTypeDialogOpen, setDeleteDocTypeDialogOpen] = React.useState(false);
  const [deletingDocType, setDeletingDocType] = React.useState(null);
  const [deleteDocTypeLoading, setDeleteDocTypeLoading] = React.useState(false);
  const [deleteDocTypeError, setDeleteDocTypeError] = React.useState(null);

  // Accordion state
  const [expanded, setExpanded] = React.useState('terms');

  // Reset all state when drawer closes or fundData changes
  React.useEffect(() => {
    if (!open) {
      // Reset all state when drawer closes
      setTerms([]);
      setTermsError(null);
      setDocumentTypesData([]);
      setDocumentTypesError(null);
      setSelectedDocTypeRow(null);
      setSelectedTermRow(null);
      setAddTermDialogOpen(false);
      setEditTermDialogOpen(false);
      setAddDocTypeDialogOpen(false);
      setEditDocTypeDialogOpen(false);
      setNewTerm('');
      setNewTermType('include');
      setEditingTerm(null);
      setWorkflowOptions([]);
      setSelectedWorkflow('');
      setWorkflowError(null);
      setEditTermValue('');
      setEditTermType('include');
      setNewDocType('');
      setNewParserId('');
      setNewParserName('');
      setNewExtractorName('');
      setEditingDocType(null);
      setEditParserId('');
      setEditParserName('');
      setEditExtractorName('');
      setAddTermError(null);
      setEditTermError(null);
      setAddDocTypeError(null);
      setEditDocTypeError(null);
      setDeleteDocTypeDialogOpen(false);
      setDeletingDocType(null);
      setDeleteDocTypeError(null);
      setParserOptions([]);
      setParserOptionsError(null);
      setExpanded('terms');
    }
  }, [open]);

  // Reset state when fundData changes
  React.useEffect(() => {
    if (open && fundData?.id) {
      setTerms([]);
      setTermsError(null);
      setDocumentTypesData([]);
      setDocumentTypesError(null);
      setSelectedDocTypeRow(null);
      setSelectedTermRow(null);
      setParserOptions([]);
      setParserOptionsError(null);
    }
  }, [fundData?.id, open]);

  // Fetch document type options for dropdown
  const fetchDocumentTypeOptions = async () => {
    if (!fundData?.tenantId) return;

    setDocumentTypeOptionsLoading(true);
    setDocumentTypeOptionsError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/document-types/by-tenant/basic?tenantId=${fundData.tenantId}&category=WF`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errorText || resp.statusText}`);
      }

      let data = await resp.json();
      // Filter out the 'Unidentified' document type
      if (Array.isArray(data)) {
        data = data.filter(item => item.name !== 'Unidentified');
      }
      setDocumentTypeOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching document type options:', err);
      setDocumentTypeOptionsError(err.message || 'Failed to load document type options');
      setDocumentTypeOptions([]);
    } finally {
      setDocumentTypeOptionsLoading(false);
    }
  };

  // Fetch parser options for dropdown
  const fetchParserOptions = async () => {
    if (!fundData?.tenantId) return;

    setParserOptionsLoading(true);
    setParserOptionsError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/master-parsers/tenant/${fundData.tenantId}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errorText || resp.statusText}`);
      }

      const data = await resp.json();
      setParserOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching parser options:', err);
      setParserOptionsError(err.message || 'Failed to load parser options');
      setParserOptions([]);
    } finally {
      setParserOptionsLoading(false);
    }
  };

  // No need to fetch extractor options as we're using a hardcoded value

  // Fetch document type, parser, and extractor options when dialogs open
  React.useEffect(() => {
    if (!addDocTypeDialogOpen && !editDocTypeDialogOpen) return;
    fetchDocumentTypeOptions();
    fetchParserOptions();
  }, [addDocTypeDialogOpen, editDocTypeDialogOpen, fundData?.tenantId]);

  // Fetch both include and exclude terms
  const fetchTerms = async () => {
    if (!fundData?.id) return;

    setTermsLoading(true);
    setTermsError(null);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';

      // Fetch include terms
      const includeResp = await fetch(`${baseUrl}conductor/funds/${fundData.id}/include-terms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Fetch exclude terms
      const excludeResp = await fetch(`${baseUrl}conductor/funds/${fundData.id}/exclude-terms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!includeResp.ok) throw new Error(`Failed to fetch include terms: ${includeResp.status} ${includeResp.statusText}`);
      if (!excludeResp.ok) throw new Error(`Failed to fetch exclude terms: ${excludeResp.status} ${excludeResp.statusText}`);

      const includeData = await includeResp.json();
      const excludeData = await excludeResp.json();

      // Combine both arrays with type information
      const combinedTerms = [
        ...(Array.isArray(includeData) ? includeData.map((term, idx) => ({
          id: `include_${idx}`,
          term,
          type: 'include'
        })) : []),
        ...(Array.isArray(excludeData) ? excludeData.map((term, idx) => ({
          id: `exclude_${idx}`,
          term,
          type: 'exclude'
        })) : [])
      ];

      setTerms(combinedTerms);
    } catch (err) {
      setTermsError(err.message);
      setTerms([]);
    } finally {
      setTermsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!open || !fundData?.id) return;
    fetchTerms();
  }, [open, fundData?.id]);

  // PUT handler for adding term
  const handleAddTerm = async () => {
    if (!canAdd) return;
    if (!newTerm.trim()) return;
    setAddTermLoading(true);
    setAddTermError(null);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';

      // Get current terms of the same type
      const currentTermsOfType = terms
        .filter(t => t.type === newTermType)
        .map(t => t.term);

      // Add new term to the array
      const updatedTerms = [...currentTermsOfType, newTerm.trim()];

      // Send to appropriate endpoint
      const endpoint = newTermType === 'include' ? 'include-terms' : 'exclude-terms';
      const resp = await fetch(`${baseUrl}conductor/funds/${fundData.id}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTerms),
      });

      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to update ${newTermType} terms: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }

      // Log the action using centralized logger
      const fundName = fundData.fundName || fundData.id;
      await AuditLoggers.termAdded(newTerm.trim(), newTermType, fundName)
        .catch(err => console.error('Error logging audit trail:', err));

      // Refresh the terms list
      await fetchTerms();

      // Reset dialog
      setAddTermDialogOpen(false);
      setNewTerm('');
      setNewTermType('include');
      showSuccess('Term added successfully');

    } catch (err) {
      setAddTermError(err.message);
      showError(err.message, 'Add Term Error');
    } finally {
      setAddTermLoading(false);
    }
  };

  // PUT handler for editing term
  const handleEditTerm = async () => {
    if (!canEdit) return;
    if (!editTermValue.trim() || !editingTerm) return;
    setEditTermLoading(true);
    setEditTermError(null);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';

      const oldType = editingTerm.type;
      const oldTerm = editingTerm.term;
      const newType = editTermType;
      const newTerm = editTermValue.trim();

      // If type changed, we need to remove from old type and add to new type
      if (oldType !== newType) {
        // Remove from old type
        const oldTypeTerms = terms
          .filter(t => t.type === oldType && t.term !== oldTerm)
          .map(t => t.term);

        // Add to new type (including the updated term)
        const newTypeTerms = [
          ...terms.filter(t => t.type === newType).map(t => t.term),
          newTerm
        ];

        // Update both endpoints
        const oldEndpoint = oldType === 'include' ? 'include-terms' : 'exclude-terms';
        const newEndpoint = newType === 'include' ? 'include-terms' : 'exclude-terms';

        // Remove from old type
        await fetch(`${baseUrl}conductor/funds/${fundData.id}/${oldEndpoint}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(oldTypeTerms),
        });

        // Add to new type
        await fetch(`${baseUrl}conductor/funds/${fundData.id}/${newEndpoint}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newTypeTerms),
        });
      } else {
        // Same type, just update the term value
        const updatedTerms = terms
          .filter(t => t.type === oldType)
          .map(t => t.term === oldTerm ? newTerm : t.term);

        const endpoint = oldType === 'include' ? 'include-terms' : 'exclude-terms';
        await fetch(`${baseUrl}conductor/funds/${fundData.id}/${endpoint}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedTerms),
        });
      }

      // Log the edit action using centralized logger
      const fundName = fundData.fundName || fundData.id;
      if (oldType !== newType) {
        await AuditLoggers.termMoved(oldTerm, newTerm, oldType, newType, fundName)
          .catch(err => console.error('Error logging audit trail:', err));
      } else {
        await AuditLoggers.termUpdated(oldTerm, newTerm, newType, fundName)
          .catch(err => console.error('Error logging audit trail:', err));
      }

      // Refresh the terms list
      await fetchTerms();

      // Reset dialog
      setEditTermDialogOpen(false);
      setEditingTerm(null);
      setEditTermValue('');
      setEditTermType('include');
      showSuccess('Term updated successfully');

    } catch (err) {
      setEditTermError(err.message);
      showError(err.message, 'Edit Term Error');
    } finally {
      setEditTermLoading(false);
    }
  };

  // Handler to open delete dialog
  const handleDeleteTerm = (term, termType) => {
    if (!canDelete) return;
    if (!fundData?.id || !term) return;
    setTermToDelete(term);
    setTermToDeleteType(termType);
    setDeleteTermDialogOpen(true);
  };

  // PUT handler for deleting term
  const handleConfirmDeleteTerm = async () => {
    if (!canDelete) return;
    if (!fundData?.id || !termToDelete) return;
    
    try {
      setDeleteTermLoading(true);
      setDeleteTermDialogOpen(false);
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';

      // Get current terms of the same type, excluding the one to delete
      const updatedTerms = terms
        .filter(t => !(t.type === termToDeleteType && t.term === termToDelete))
        .filter(t => t.type === termToDeleteType)
        .map(t => t.term);

      // Send to appropriate endpoint
      const endpoint = termToDeleteType === 'include' ? 'include-terms' : 'exclude-terms';
      const resp = await fetch(`${baseUrl}conductor/funds/${fundData.id}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTerms),
      });

      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to delete term: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }

      // Log the delete action using centralized logger
      const fundName = fundData.fundName || fundData.id;
      await AuditLoggers.custom(
        `Deleted ${termToDeleteType} term '${termToDelete}' from fund '${fundName}'`,
        `Delete ${termToDeleteType === 'include' ? 'Include' : 'Exclude'} Term for Fund`,
        'handleConfirmDeleteTerm',
        {},
        'Master Data Management'
      ).catch(err => console.error('Error logging audit trail:', err));

      // Refresh the terms list
      await fetchTerms();
      
      setTermToDelete(null);
      setTermToDeleteType(null);
      setSelectedTermRow(null);
      showSuccess('Term deleted successfully');
    } catch (err) {
      console.error('Error deleting term:', err);
      showError(err.message, 'Delete Term Error');
    } finally {
      setDeleteTermLoading(false);
    }
  };

  // Handle term type change directly in the grid
  const handleTermTypeChange = async (termId, newType) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';

      // Find the term being changed
      const termToChange = terms.find(t => t.id === termId);
      if (!termToChange) return;

      const oldType = termToChange.type;
      const termValue = termToChange.term;

      // Remove from old type
      const oldTypeTerms = terms
        .filter(t => t.type === oldType && t.id !== termId)
        .map(t => t.term);

      // Add to new type
      const newTypeTerms = [
        ...terms.filter(t => t.type === newType).map(t => t.term),
        termValue
      ];

      // Update both endpoints
      const oldEndpoint = oldType === 'include' ? 'include-terms' : 'exclude-terms';
      const newEndpoint = newType === 'include' ? 'include-terms' : 'exclude-terms';

      // Remove from old type
      await fetch(`${baseUrl}conductor/funds/${fundData.id}/${oldEndpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(oldTypeTerms),
      });

      // Add to new type
      await fetch(`${baseUrl}conductor/funds/${fundData.id}/${newEndpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTypeTerms),
      });

      // Refresh the terms list
      await fetchTerms();

    } catch (err) {
      console.error('Error updating term type:', err);
      // You might want to show an error message to the user here
    }
  };

  // Helper function to extract parser ID from extractorMetadata
  const extractParserId = (extractorMetadata) => {
    if (!extractorMetadata) return 'N/A';
    if (typeof extractorMetadata === 'string') return extractorMetadata;
    if (typeof extractorMetadata === 'object') {
      // Handle different possible formats
      if (extractorMetadata.parser_id) return extractorMetadata.parser_id;
      if (extractorMetadata.parserId) return extractorMetadata.parserId;
      if (extractorMetadata.id) return extractorMetadata.id;
      // If it's an object but doesn't have expected properties, convert to string
      return JSON.stringify(extractorMetadata);
    }
    return 'N/A';
  };

  // Function to fetch document types
  const fetchDocumentTypes = async () => {
    if (!fundData?.id) return;

    setDocumentTypesLoading(true);
    setDocumentTypesError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const tenantId = fundData.tenantId;
      const fundId = fundData.id;
      const url = `${baseUrl}/conductor/extraction-configurations/by-tenant-and-fund?tenantId=${tenantId}&fundId=${fundId}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errorText || resp.statusText}`);
      }

      const data = await resp.json();
      const items = Array.isArray(data) ? data : [];

      console.log('Document types items:', items); // Debug log

      // First, collect all unique workflow IDs to fetch their names
      const workflowIds = [...new Set(items
        .map(item => item.workflowDefinitionSnowflakeId)
        .filter(Boolean)
      )];

      // Create a map of workflow IDs to their names
      const workflowMap = {};

      // Fetch workflow names in parallel with better error handling
      await Promise.all(workflowIds.map(async (workflowId) => {
        try {
          const workflowUrl = `${baseUrl}/conductor/workflow-definition-snowflake/${workflowId}`;
          const workflowResp = await fetch(workflowUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (workflowResp.ok) {
            const workflowData = await workflowResp.json();
            if (workflowData && workflowData.name) {
              workflowMap[workflowId] = workflowData.name;
            }
          } else if (workflowResp.status === 404 || workflowResp.status === 500) {
            // If workflow not found or server error, use a simplified display
            console.warn(`Workflow definition not found or error for ID: ${workflowId}`, 
              { status: workflowResp.status, statusText: workflowResp.statusText });
            // Show first 8 chars of ID for brevity
            const shortId = workflowId.length > 8 ? `${workflowId.substring(0, 4)}...${workflowId.slice(-4)}` : workflowId;
            workflowMap[workflowId] = `Workflow (${shortId})`;
          } else {
            console.error(`Error fetching workflow ${workflowId}:`, workflowResp.status, workflowResp.statusText);
            // Show first 8 chars of ID for brevity
            const shortId = workflowId.length > 8 ? `${workflowId.substring(0, 4)}...${workflowId.slice(-4)}` : workflowId;
            workflowMap[workflowId] = `Workflow (${shortId})`;
          }
        } catch (err) {
          console.error(`Error processing workflow ${workflowId}:`, err);
          workflowMap[workflowId] = `Error: ${err.message || 'Failed to load'}`;
        }
      }));

      setDocumentTypesData(items.map((item) => {
        const docTypeObj = documentTypeOptions.find(dt => dt.id === item.documentTypeId);
        const workflowName = item.workflowDefinitionSnowflakeId
          ? (workflowMap[item.workflowDefinitionSnowflakeId] || item.workflowDefinitionSnowflakeId)
          : 'None';

        return {
          id: item.id,
          docType: docTypeObj ? docTypeObj.name : 'Unknown',
          extractorName: item.extractorName || 'Unknown',
          parserId: extractParserId(item.extractorMetadata),
          workflowName: workflowName,
          workflowId: item.workflowDefinitionSnowflakeId,
          rawData: item, // Store raw data for editing
        };
      }));

    } catch (err) {
      console.error('Error fetching document types:', err);
      setDocumentTypesError(err.message || 'Failed to load document types');
      setDocumentTypesData([]);
    } finally {
      setDocumentTypesLoading(false);
    }
  };

  // Fetch document type options when drawer opens
  React.useEffect(() => {
    if (!open || !fundData?.tenantId) return;
    fetchDocumentTypeOptions();
  }, [open, fundData?.tenantId]);

  // Fetch document types when the drawer is first opened
  const hasFetchedInitialData = React.useRef(false);
  React.useEffect(() => {
    if (open && !hasFetchedInitialData.current && fundData?.id && documentTypeOptions.length > 0) {
      fetchDocumentTypes();
      hasFetchedInitialData.current = true;
    }
    
    // Reset the flag when the drawer is closed
    return () => {
      if (!open) {
        hasFetchedInitialData.current = false;
      }
    };
  }, [open, fundData?.id, documentTypeOptions]);


  // Fetch workflows based on document type
  const fetchWorkflows = async (documentTypeId) => {
    if (!documentTypeId) return [];

    setWorkflowLoading(true);
    setWorkflowError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${documentTypeId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const workflows = Array.isArray(data) ? data : [];
      setWorkflowOptions(workflows);
      return workflows; // Return the workflows data
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setWorkflowError(err.message || 'Failed to load workflows');
      setWorkflowOptions([]);
      return [];
    } finally {
      setWorkflowLoading(false);
    }
  };

  // Handle document type change in the add dialog
  const handleDocTypeChange = (e) => {
    const docTypeName = e.target.value;
    setNewDocType(docTypeName);

    // Reset selected workflow and parser type when document type changes
    setSelectedWorkflow('');
    setSelectedWorkflowParserType(null);
    setNewParserId('');
    setNewParserName('');

    // Find the selected document type to get its ID
    const selectedDocType = documentTypeOptions.find(doc => doc.name === docTypeName);
    if (selectedDocType) {
      // Fetch workflows for the selected document type
      fetchWorkflows(selectedDocType.id);
    }
  };

  // Columns for Terms grid with read-only type display
  const termsColumns = [
    { field: 'term', headerName: 'Term', flex: 1 },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      valueFormatter: (params) => {
        return params.value === 'include' ? 'Include' : 'Exclude';
      },
      renderCell: (params) => (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: params.value === 'include' ? ht.secondaryTab : ht.listBg,
            color: params.value === 'include' ? ht.green : ht.red,
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        >
          {params.value === 'include' ? 'Include' : 'Exclude'}
        </Box>
      )
    },
  ];

  // Updated columns for Document Types grid
  const documentTypesColumns = [
    {
      field: 'docType',
      headerName: 'Document Type',
      flex: 2,
      minWidth: 150
    },
    {
      field: 'parserId',
      headerName: 'Parser ID',
      flex: 1.5,
      minWidth: 150
    },
    {
      field: 'workflowName',
      headerName: 'Workflow',
      flex: 2,
      minWidth: 200,
      valueGetter: (params) => {
        return params.row.workflowName || 'None';
      }
    },
  ];

  const handleAddDocType = async () => {
    if (!canAdd) return;
    if (!newDocType || !newDocType.trim()) return;

    setAddDocTypeLoading(true);
    setAddDocTypeError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/extraction-configurations`;

      // Find the selected document type to get its ID
      const selectedDocType = documentTypeOptions.find(doc => doc.name === newDocType.trim());
      if (!selectedDocType) {
        throw new Error('Selected document type not found');
      }

      // Include parser_id for DocParser workflows (fund-level parser_id)
      const extractorMetadata = isDocParserFamily(selectedWorkflowParserType) && newParserId 
        ? { parser_id: newParserId } 
        : {};

      // Find the selected workflow to get its workflowDefinitionSnowflakeId
      const selectedWorkflowObj = workflowOptions.find(w => w.id === selectedWorkflow);
      const workflowDefinitionSnowflakeId = selectedWorkflowObj?.workflowDefinitionSnowflakeId || null;

      const payload = {
        tenantId: fundData.tenantId,
        fundId: fundData.id,
        documentTypeId: selectedDocType.id,
        extractorName: 'doc_parser',
        workflowDefinitionSnowflakeId: workflowDefinitionSnowflakeId,
        extractorMetadata: extractorMetadata
      };

      console.log('Sending payload:', payload); // Debug log

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errorText || resp.statusText}`);
      }

      // Log the successful addition using centralized logger
      await AuditLoggers.documentTypeAdded(
        newDocType,
        fundData.fundName || fundData.id
      ).catch(err => console.error('Error logging audit trail:', err));

      await fetchDocumentTypes();
      setNewDocType('');
      setNewParserId('');
      setNewParserName('');
      setAddDocTypeDialogOpen(false);

    } catch (err) {
      console.error('Error adding document type:', err);
      setAddDocTypeError(err.message || 'Failed to add document type');
    } finally {
      setAddDocTypeLoading(false);
    }
  };

  const handleOpenAddTermDialog = (e) => {
    if (!canAdd) return;
    e.stopPropagation();
    setAddTermDialogOpen(true);
  };

  const handleOpenEditTermDialog = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (!selectedTermRow) {
      setEditTermError('Please select a row to edit');
      return;
    }
    setEditingTerm(selectedTermRow);
    setEditTermValue(selectedTermRow.term);
    setEditTermType(selectedTermRow.type);
    setEditTermDialogOpen(true);
  };

  const handleOpenAddDocTypeDialog = (e) => {
    if (!canAdd) return;
    e.stopPropagation();
    // Reset all form states
    setNewDocType('');
    setSelectedWorkflow('');
    setSelectedWorkflowParserType(null);
    setNewParserId('');
    setNewParserName('');
    setAddDocTypeError('');
    setWorkflowError('');
    setWorkflowOptions([]);
    setAddDocTypeDialogOpen(true);
  };

  const handleOpenEditDocTypeDialog = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (!selectedDocTypeRow) {
      setEditDocTypeError('Please select a row to edit');
      return;
    }

    // First set the editing document type
    setEditingDocType(selectedDocTypeRow);
    
    // Set parser information if available
    const parser = parserOptions.find(p => p.parserId === selectedDocTypeRow.parserId);
    setEditParserName(parser ? parser.parserName : '');
    setEditParserId(selectedDocTypeRow.parserId && selectedDocTypeRow.parserId !== 'N/A' ? selectedDocTypeRow.parserId : '');
    setEditExtractorName(selectedDocTypeRow.extractorName || '');
    
    // Find the document type to get its ID for fetching workflows
    const docType = documentTypeOptions.find(dt => dt.name === selectedDocTypeRow.docType);
    
    if (docType) {
      // Set workflow information if available
      const workflowToSelect = selectedDocTypeRow.rawData?.workflowDefinitionSnowflakeId;
      
      // Only fetch workflows if we don't have any or if the document type has changed
      if (workflowOptions.length === 0 || 
          (workflowOptions[0]?.documentTypeId !== docType.id)) {
        
        console.log('Fetching workflows for document type:', docType.id);
        setWorkflowLoading(true);
        setWorkflowError(null);
        
        // Fetch workflows for the document type and handle the response
        fetchWorkflows(docType.id)
          .then((workflows) => {
            console.log('Workflows fetched. Available options:', workflows);
            if (workflowToSelect) {
              console.log('Looking for workflow with snowflake ID:', workflowToSelect);
              // Use the workflows from the response to find the matching workflow
              const workflow = workflows.find(w => {
                const found = w.workflowDefinitionSnowflakeId === workflowToSelect;
                console.log('Checking workflow:', w.id, 'snowflake:', w.workflowDefinitionSnowflakeId, 'match:', found);
                return found;
              });
              
              if (workflow) {
                console.log('Found matching workflow:', workflow);
                setSelectedWorkflow(workflow.id);
                setSelectedWorkflowParserType(workflow.parserType);
                console.log('Set selected workflow to:', workflow.id);
              } else {
                console.log('No matching workflow found for snowflake ID:', workflowToSelect);
                setSelectedWorkflow('');
              }
            }
          })
          .catch(err => {
            console.error('Error fetching workflows:', err);
            setWorkflowError('Failed to load workflows');
          })
          .finally(() => {
            setWorkflowLoading(false);
          });
      } else if (workflowToSelect) {
        console.log('Using existing workflows. Looking for:', workflowToSelect);
        console.log('Available workflow options:', workflowOptions);
        // We already have the workflows, just find and set the selected one
        const workflow = workflowOptions.find(w => {
          const found = w.workflowDefinitionSnowflakeId === workflowToSelect;
          console.log('Checking existing workflow:', w.id, 'snowflake:', w.workflowDefinitionSnowflakeId, 'match:', found);
          return found;
        });
        
        if (workflow) {
          console.log('Found existing workflow match:', workflow);
          setSelectedWorkflow(workflow.id);
          setSelectedWorkflowParserType(workflow.parserType);
          console.log('Set selected workflow to:', workflow.id);
        } else {
          console.log('No matching workflow found in existing options');
          setSelectedWorkflow('');
        }
      } else {
        setSelectedWorkflow('');
      }
    } else {
      setSelectedWorkflow('');
    }
    
    setEditDocTypeDialogOpen(true);
  };

  // Handle opening delete dialog
  const handleOpenDeleteDocTypeDialog = (e) => {
    if (!canDelete) return;
    e.stopPropagation();
    if (!selectedDocTypeRow) {
      setDeleteDocTypeError('Please select a row to delete');
      return;
    }
    setDeletingDocType(selectedDocTypeRow);
    setDeleteDocTypeDialogOpen(true);
    setDeleteDocTypeError(null);
  };

  // Handle delete document type
  const handleDeleteDocType = async () => {
    if (!canDelete) return;
    if (!deletingDocType) return;

    setDeleteDocTypeLoading(true);
    setDeleteDocTypeError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/extraction-configurations/${deletingDocType.id}`;

      console.log('Deleting document type configuration:', deletingDocType.id);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      console.log('Document type configuration deleted successfully');
      
      // Log the deletion using centralized logger
      await AuditLoggers.documentTypeDeleted(
        deletingDocType.docType,
        fundData.fundName || fundData.id
      ).catch(err => console.error('Error logging audit trail:', err));
      
      // Refresh the document types list
      await fetchDocumentTypes();
      
      // Close dialog and reset state
      setDeleteDocTypeDialogOpen(false);
      setDeletingDocType(null);
      setSelectedDocTypeRow(null);

    } catch (err) {
      console.error('Error deleting document type:', err);
      setDeleteDocTypeError(err.message || 'Failed to delete document type');
    } finally {
      setDeleteDocTypeLoading(false);
    }
  };

  // Handle document type change in the edit dialog
  const handleEditDocTypeChange = (e) => {
    const docTypeName = e.target.value;
    setEditingDocType(prev => ({ ...prev, docType: docTypeName }));

    // Reset selected workflow when document type changes
    setSelectedWorkflow('');

    // Find the document type to get its ID for fetching workflows
    const docType = documentTypeOptions.find(dt => dt.name === docTypeName);
    if (docType) {
      // Reset workflow options and loading state
      setWorkflowOptions([]);
      setWorkflowLoading(true);
      setWorkflowError(null);

      // Fetch workflows for the document type
      fetchWorkflows(docType.id)
        .catch(err => {
          console.error('Error fetching workflows:', err);
          setWorkflowError('Failed to load workflows');
        })
        .finally(() => {
          setWorkflowLoading(false);
        });
    } else {
      setSelectedWorkflow('');
      setWorkflowOptions([]);
    }
  };

  const handleEditDocTypeSubmit = async () => {
    if (!canEdit) return;
    setEditDocTypeLoading(true);
    setEditDocTypeError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const baseUrl = (process.env.REACT_APP_BACKEND_URL).replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/extraction-configurations/${editingDocType.id}`;

      const extractorMetadata = isDocParserFamily(selectedWorkflowParserType) && editParserId 
        ? { parser_id: editParserId } 
        : {};

      // Get the document type ID for the current document type
      const docType = documentTypeOptions.find(dt => dt.name === editingDocType.docType);
      if (!docType) {
        throw new Error('Document type not found');
      }

      // Find the selected workflow to get its workflowDefinitionSnowflakeId
      const selectedWorkflowObj = workflowOptions.find(w => w.id === selectedWorkflow);
      const workflowDefinitionSnowflakeId = selectedWorkflowObj?.workflowDefinitionSnowflakeId || null;

      const payload = {
        documentTypeId: docType.id,
        extractorName: 'doc_parser',
        workflowDefinitionSnowflakeId: workflowDefinitionSnowflakeId,
        extractorMetadata: extractorMetadata
      };

      console.log('Editing payload:', payload);

      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${errorText || resp.statusText}`);
      }

      // Log the successful update using centralized logger
      await AuditLoggers.documentTypeUpdated(
        editingDocType.docType,
        fundData.fundName || fundData.id
      ).catch(err => console.error('Error logging audit trail:', err));

      await fetchDocumentTypes();
      setEditDocTypeDialogOpen(false);
      setEditingDocType(null);
      setEditParserId('');
      setEditParserName('');
      setEditExtractorName('');
      setSelectedWorkflow('');

    } catch (err) {
      console.error('Error editing document type:', err);
      setEditDocTypeError(err.message || 'Failed to edit document type');
    } finally {
      setEditDocTypeLoading(false);
    }
  };


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0 }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
          {fundData?.fundName || 'Fund Details'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        {/* Terms Accordion */}
        <Accordion
          expanded={expanded === 'terms'}
          onChange={() => setExpanded(expanded === 'terms' ? false : 'terms')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Include/Exclude Terms</Typography>
            <Tooltip title={canEdit ? 'Edit Term' : 'No edit permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditTermDialog(e);
                  }}
                  size="small"
                  disabled={!selectedTermRow || !canEdit}
                  sx={harmonateDrawerEditIconSx}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete Term' : 'No delete permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedTermRow) {
                      handleDeleteTerm(selectedTermRow.term, selectedTermRow.type);
                    }
                  }}
                  size="small"
                  disabled={!selectedTermRow || deleteTermLoading || !canDelete}
                  sx={harmonateDrawerDeleteIconSx}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Term' : 'No add permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddTermDialog(e);
                  }}
                  size="small"
                  disabled={!canAdd}
                  sx={harmonateDrawerAddIconSx}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 300,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {termsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress sx={{ color: ht.blue }} />
              </Box>
            ) : termsError ? (
              <Alert severity="error">{termsError}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
                <DataGridPro
                  rows={terms}
                  columns={termsColumns}
                  columnResizeMode="onEnd"
                  resizable
                  sx={{
                    ...harmonateDataGridSx,
                    flex: 1,
                    '& .MuiDataGrid-cell': {
                      color: ht.text,
                    },
                  }}
                  getRowId={(row) => row.id}
                  rowHeight={35}
                  onRowSelectionModelChange={(newSelection) => {
                    if (newSelection.length > 0) {
                      const selectedRow = terms.find(row => row.id === newSelection[0]);
                      setSelectedTermRow(selectedRow);
                    } else {
                      setSelectedTermRow(null);
                    }
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography sx={{ color: ht.text }}>No terms found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Document Types Accordion */}
        <Accordion
          expanded={expanded === 'documentTypes'}
          onChange={() => setExpanded(expanded === 'documentTypes' ? false : 'documentTypes')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Document Types</Typography>
            <Tooltip title={canEdit ? 'Edit Document Type' : 'No edit permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditDocTypeDialog(e);
                  }}
                  size="small"
                  disabled={!selectedDocTypeRow || !canEdit}
                  sx={harmonateDrawerEditIconSx}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete Document Type' : 'No delete permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDocTypeDialog(e);
                  }}
                  size="small"
                  disabled={!selectedDocTypeRow || !canDelete}
                  sx={harmonateDrawerDeleteIconSx}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Document Type' : 'No add permission'}>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddDocTypeDialog(e);
                  }}
                  size="small"
                  disabled={!canAdd}
                  sx={harmonateDrawerAddIconSx}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 300,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {documentTypesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress sx={{ color: ht.blue }} />
              </Box>
            ) : documentTypesError ? (
              <Alert severity="error">{documentTypesError}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
                <DataGridPro
                  rows={documentTypesData}
                  columns={documentTypesColumns}
                  columnResizeMode="onEnd"
                  resizable
                  sx={{
                    ...harmonateDataGridSx,
                    flex: 1,
                    '& .MuiDataGrid-cell': {
                      color: ht.text,
                    },
                  }}
                  getRowId={(row) => row.id}
                  rowHeight={35}
                  onRowSelectionModelChange={(newSelection) => {
                    if (newSelection.length > 0) {
                      const selectedRow = documentTypesData.find(row => row.id === newSelection[0]);
                      setSelectedDocTypeRow(selectedRow);
                    } else {
                      setSelectedDocTypeRow(null);
                    }
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography sx={{ color: ht.text }}>No document types found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>

      {/* Add Term Dialog */}
      <Dialog open={addTermDialogOpen} onClose={() => setAddTermDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Term</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            autoFocus
            margin="dense"
            label="Term"
            type="text"
            fullWidth
            size="small"
            value={newTerm}
            onChange={e => setNewTerm(e.target.value)}
            disabled={addTermLoading}
            error={!!addTermError}
            helperText={addTermError}
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          />
          <FormControl fullWidth size="small" sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="term-type-label">Type</InputLabel>
            <Select
              labelId="term-type-label"
              label="Type"
              value={newTermType}
              onChange={e => setNewTermType(e.target.value)}
              disabled={addTermLoading}
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="include">Include</MenuItem>
              <MenuItem value="exclude">Exclude</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddTermDialogOpen(false)} disabled={addTermLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTerm}
            variant="contained"
            sx={harmonateProceedButtonSx}
            disabled={addTermLoading || !newTerm.trim() || !canAdd}
          >
            {addTermLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Term Dialog */}
      <Dialog open={editTermDialogOpen} onClose={() => setEditTermDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Edit Term</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {editingTerm && (
            <Typography sx={{ fontSize: '0.875rem', color: ht.text, mb: 1 }}>
              Editing: <strong>{editingTerm.term}</strong> ({editingTerm.type})
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Term"
            type="text"
            fullWidth
            size="small"
            value={editTermValue}
            onChange={e => setEditTermValue(e.target.value)}
            disabled={editTermLoading}
            error={!!editTermError}
            helperText={editTermError}
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          />
          <FormControl fullWidth size="small" sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="edit-term-type-label">Type</InputLabel>
            <Select
              labelId="edit-term-type-label"
              label="Type"
              value={editTermType}
              onChange={e => setEditTermType(e.target.value)}
              disabled={editTermLoading}
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="include">Include</MenuItem>
              <MenuItem value="exclude">Exclude</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditTermDialogOpen(false)} disabled={editTermLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleEditTerm}
            variant="contained"
            sx={harmonateProceedButtonSx}
            disabled={editTermLoading || !editTermValue.trim() || !canEdit}
          >
            {editTermLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Term Confirmation Dialog */}
      <Dialog open={deleteTermDialogOpen} onClose={() => setDeleteTermDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Are you sure you want to delete the term "{termToDelete}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTermDialogOpen(false)} disabled={deleteTermLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteTerm}
            variant="contained"
            disabled={deleteTermLoading || !canDelete}
            sx={harmonateAbortButtonSx}
          >
            {deleteTermLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Document Type Dialog */}
      <Dialog open={addDocTypeDialogOpen} onClose={() => setAddDocTypeDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Document Type</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <FormControl fullWidth size="small" error={!!addDocTypeError} sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="document-type-label">Document Type</InputLabel>
            <Select
              labelId="document-type-label"
              label="Document Type"
              value={newDocType}
              onChange={handleDocTypeChange}
              disabled={addDocTypeLoading || documentTypeOptionsLoading}
              MenuProps={harmonateSelectMenuProps}
            >
              {documentTypeOptionsLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : documentTypeOptionsError ? (
                <MenuItem disabled>Error: {documentTypeOptionsError}</MenuItem>
              ) : documentTypeOptions.length === 0 ? (
                <MenuItem disabled>No document types available</MenuItem>
              ) : (
                documentTypeOptions.map((docType) => (
                  <MenuItem key={docType.id} value={docType.name}>
                    {docType.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" error={!!workflowError} sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="workflow-label">Workflow</InputLabel>
            <Select
              labelId="workflow-label"
              label="Workflow"
              value={selectedWorkflow}
              onChange={e => {
                const selectedWorkflowId = e.target.value;
                setSelectedWorkflow(selectedWorkflowId);
                
                // Find the selected workflow to get its parserType
                const workflow = workflowOptions.find(w => w.id === selectedWorkflowId);
                if (workflow) {
                  console.log('Selected workflow:', workflow); // Debug log
                  setSelectedWorkflowParserType(workflow.parserType);
                  
                  if (!isDocParserFamily(workflow.parserType)) {
                    setNewParserId('');
                    setNewParserName('');
                  }
                } else {
                  console.log('No workflow found with id:', selectedWorkflowId); // Debug log
                  setSelectedWorkflowParserType(null);
                }
              }}
              disabled={workflowLoading || !newDocType}
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="">
                <em>No Workflow</em>
              </MenuItem>
              {workflowLoading ? (
                <MenuItem disabled>Loading workflows...</MenuItem>
              ) : workflowError ? (
                <MenuItem disabled>Error: {workflowError}</MenuItem>
              ) : workflowOptions.length === 0 ? (
                <MenuItem disabled>No workflows available</MenuItem>
              ) : (
                workflowOptions.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.workflowDefinitionName || `Workflow ${workflow.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
            {workflowError && (
              <Typography color="error" variant="caption">{workflowError}</Typography>
            )}
          </FormControl>

          {/* Hidden Extractor field with default value */}
          <input type="hidden" value={extractorOptions[0]?.extractorName || ''} onChange={(e) => setNewExtractorName(e.target.value)} />

          {isDocParserFamily(selectedWorkflowParserType) && (
            <>
              <Autocomplete
                fullWidth
                options={parserOptions}
                getOptionLabel={(option) => option.parserName || ''}
                value={parserOptions.find(p => p.parserName === newParserName) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setNewParserName(newValue.parserName);
                    setNewParserId(newValue.parserId);
                  } else {
                    setNewParserName('');
                    setNewParserId('');
                  }
                }}
                disabled={addDocTypeLoading || parserOptionsLoading}
                loading={parserOptionsLoading}
                loadingText="Loading parsers..."
                noOptionsText={parserOptionsError ? `Error: ${parserOptionsError}` : "No parsers available"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parser Name"
                    size="small"
                    error={!!parserOptionsError}
                    helperText="Search and select a parser or enter a custom Parser ID below"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.parserName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.parserId}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              <TextField
                label="Parser ID"
                value={newParserId}
                onChange={e => setNewParserId(e.target.value)}
                disabled={addDocTypeLoading}
                fullWidth
                size="small"
                helperText="Enter the parser ID for the selected workflow"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </>
          )}

          {addDocTypeError && (
            <Alert severity="error">
              {addDocTypeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAddDocTypeDialogOpen(false)}
            disabled={addDocTypeLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDocType}
            variant="contained"
            sx={harmonateProceedButtonSx}
            disabled={addDocTypeLoading || !newDocType || documentTypeOptionsLoading || extractorOptionsLoading || workflowLoading || !canAdd}
          >
            {addDocTypeLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Type Dialog */}
      <Dialog open={editDocTypeDialogOpen} onClose={() => setEditDocTypeDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Edit Document Type Configuration</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {editingDocType && (
            <Typography sx={{ fontSize: '0.875rem', color: ht.text, mb: 1 }}>
              Editing: <strong>{editingDocType.docType}</strong>
            </Typography>
          )}

          <FormControl fullWidth size="small" sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="edit-document-type-label">Document Type</InputLabel>
            <Select
              labelId="edit-document-type-label"
              label="Document Type"
              value={editingDocType?.docType || ''}
              onChange={handleEditDocTypeChange}
              disabled={editDocTypeLoading || documentTypeOptionsLoading}
              MenuProps={harmonateSelectMenuProps}
            >
              {documentTypeOptionsLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : documentTypeOptionsError ? (
                <MenuItem disabled>Error: {documentTypeOptionsError}</MenuItem>
              ) : documentTypeOptions.length === 0 ? (
                <MenuItem disabled>No document types available</MenuItem>
              ) : (
                documentTypeOptions.map((docType) => (
                  <MenuItem key={docType.id} value={docType.name}>
                    {docType.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" error={!!workflowError} sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps} id="edit-workflow-label">Workflow</InputLabel>
            <Select
              labelId="edit-workflow-label"
              label="Workflow"
              value={selectedWorkflow}
              onChange={e => {
                const selectedWorkflowId = e.target.value;
                setSelectedWorkflow(selectedWorkflowId);
                
                // Find the selected workflow to get its parserType
                const workflow = workflowOptions.find(w => w.id === selectedWorkflowId);
                if (workflow) {
                  console.log('Selected workflow:', workflow); // Debug log
                  setSelectedWorkflowParserType(workflow.parserType);
                  
                  if (!isDocParserFamily(workflow.parserType)) {
                    setEditParserId('');
                    setEditParserName('');
                  }
                } else {
                  console.log('No workflow found with id:', selectedWorkflowId); // Debug log
                  setSelectedWorkflowParserType(null);
                }
              }}
              disabled={workflowLoading || !editingDocType?.docType}
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="">
                <em>No Workflow</em>
              </MenuItem>
              {workflowLoading ? (
                <MenuItem disabled>Loading workflows...</MenuItem>
              ) : workflowError ? (
                <MenuItem disabled>Error: {workflowError}</MenuItem>
              ) : workflowOptions.length === 0 ? (
                <MenuItem disabled>No workflows available</MenuItem>
              ) : (
                workflowOptions.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.workflowDefinitionName || `Workflow ${workflow.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
            {workflowError && (
              <Typography color="error" variant="caption">{workflowError}</Typography>
            )}
          </FormControl>

          {/* Parser fields for DOC_PARSER workflow type */}
          {isDocParserFamily(selectedWorkflowParserType) && (
            <>
              <Autocomplete
                fullWidth
                options={parserOptions}
                getOptionLabel={(option) => option.parserName || ''}
                value={parserOptions.find(p => p.parserId === editParserId) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setEditParserName(newValue.parserName);
                    setEditParserId(newValue.parserId);
                  } else {
                    setEditParserName('');
                    setEditParserId('');
                  }
                }}
                disabled={editDocTypeLoading || parserOptionsLoading}
                loading={parserOptionsLoading}
                loadingText="Loading parsers..."
                noOptionsText={parserOptionsError ? `Error: ${parserOptionsError}` : "No parsers available"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parser Name"
                    size="small"
                    error={!!parserOptionsError}
                    helperText="Search and select a parser or enter a custom Parser ID below"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.parserName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.parserId}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              <TextField
                label="Parser ID"
                value={editParserId}
                onChange={e => setEditParserId(e.target.value)}
                disabled={editDocTypeLoading}
                fullWidth
                size="small"
                helperText="Enter the parser ID for the selected workflow"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </>
          )}

          {editDocTypeError && (
            <Alert severity="error">
              {editDocTypeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEditDocTypeDialogOpen(false)}
            disabled={editDocTypeLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditDocTypeSubmit}
            variant="contained"
            sx={harmonateProceedButtonSx}
            disabled={editDocTypeLoading || !canEdit}
          >
            {editDocTypeLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Type Dialog */}
      <Dialog
        open={deleteDocTypeDialogOpen}
        onClose={() => setDeleteDocTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Delete Document Type Configuration</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {deletingDocType && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', color: ht.text, mb: 1 }}>
                Are you sure you want to delete this document type configuration?
              </Typography>
              <Box sx={{ p: 2, bgcolor: ht.listBg, borderRadius: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', color: ht.text }}><strong>Document Type:</strong> {deletingDocType.docType}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: ht.text }}><strong>Parser ID:</strong> {deletingDocType.parserId}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: ht.text }}><strong>Workflow:</strong> {deletingDocType.workflowName}</Typography>
              </Box>
              <Alert severity="warning">
                This action cannot be undone. The document type configuration will be permanently deleted.
              </Alert>
              {deleteDocTypeError && (
                <Alert severity="error">
                  {deleteDocTypeError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDocTypeDialogOpen(false)}
            disabled={deleteDocTypeLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDocType}
            variant="contained"
            disabled={deleteDocTypeLoading || !canDelete}
            sx={harmonateAbortButtonSx}
          >
            {deleteDocTypeLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Drawer>
  );
};

export default MasterDataDrawer;